// A fold: one quiet row that opens what has a default or is rarely needed
// ("Advanced", "More options", "Run records"), so a screen shows only what
// the operator decides on. The row is a ghost Button: a chevron that turns
// when open, the summary, an optional "(3)" count and a quiet meta line
// with the current values ("45 min · default model · 2 GB") that ends in an
// ellipsis when it is too long. No card chrome: the caller places it.
//
//   <Disclosure summary="Advanced" meta="45 min · default model">
//     …fields…
//   </Disclosure>
//
// Uncontrolled by default (`defaultOpen`, closed unless set); pass `open`
// + `onOpenChange` when something else must open it (goTo(problem) opening
// the fold that holds the field). `id` goes on the root as an anchor: an
// uncontrolled fold also opens by itself when the page's URL hash names it
// or an element inside it (`…/settings#limits`), on load and on a hash
// change. The body stays in the DOM with `hidden` while closed, so
// anchors inside it resolve and form state inside it survives a close.

import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { ChevronRight } from "lucide-react";

import { Button } from "./Button";
import { cx } from "./controlStyles";

export interface DisclosureProps {
  /** "Advanced", "More options", "Run records". */
  summary: ReactNode;
  /** Quiet text after the summary: "45 min · default model · 2 GB". */
  meta?: ReactNode;
  /** Shown as "(3)" after the summary. */
  count?: number;
  /** Controlled: the caller owns the state. */
  open?: boolean;
  /** Uncontrolled start state (default closed). */
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** An anchor for goTo / URL hashes, on the root element. */
  id?: string;
  children: ReactNode;
  className?: string;
}

/** True when the URL hash names `root` or an element inside it. */
function hashTargets(root: HTMLElement | null): boolean {
  if (!root || typeof window === "undefined") return false;
  const name = decodeURIComponent(window.location.hash.slice(1));
  if (!name) return false;
  const target = document.getElementById(name);
  return !!target && root.contains(target);
}

export function Disclosure({ summary, meta, count, open, defaultOpen = false, onOpenChange, id, children, className }: DisclosureProps) {
  const [inner, setInner] = useState(defaultOpen);
  const controlled = open !== undefined;
  const isOpen = controlled ? open : inner;
  const root = useRef<HTMLDivElement>(null);
  const bodyId = `${useId()}-body`;

  useEffect(() => {
    if (controlled) return;
    const check = () => {
      if (hashTargets(root.current)) setInner(true);
    };
    check();
    window.addEventListener("hashchange", check);
    return () => window.removeEventListener("hashchange", check);
  }, [controlled]);

  const toggle = () => {
    const next = !isOpen;
    if (!controlled) setInner(next);
    onOpenChange?.(next);
  };

  return (
    <div ref={root} id={id} className={cx("flex min-w-0 flex-col", className)}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-expanded={isOpen}
        aria-controls={bodyId}
        onClick={toggle}
        className="-mx-1.5 w-[calc(100%+0.75rem)] min-w-0 justify-start gap-1.5 px-1.5 text-left"
      >
        <ChevronRight
          aria-hidden
          className={cx("h-3.5 w-3.5 shrink-0 text-fg-subtle transition-transform", isOpen && "rotate-90")}
          strokeWidth={2}
        />
        <span className="shrink-0 text-sm font-medium text-fg">{summary}</span>
        {count !== undefined && <span className="shrink-0 text-sm tabular-nums text-fg-muted">({count})</span>}
        {meta && <span className="min-w-0 truncate text-xs font-normal text-fg-subtle">{meta}</span>}
      </Button>
      <div id={bodyId} hidden={!isOpen} className="pt-2">
        {children}
      </div>
    </div>
  );
}
